import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Trash2, RefreshCw } from 'lucide-react';

// Format decimal minutes to HH:MM:SS
const formatMinutesToHHMMSS = (decimalMinutes) => {
  if (!decimalMinutes || isNaN(decimalMinutes)) return '00:00:00';
  const totalSeconds = Math.round(decimalMinutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

function ImportsHistory({ importHistory, onDeleteDay, onRefresh, t }) {
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  // Get current month info
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const monthStr = calendarMonth.toISOString().slice(0, 7);
  const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  const dayNames = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
  
  // Calculate calendar grid
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay();
  
  // Stats
  const totalDays = importHistory?.length || 0;
  const thisMonthDays = importHistory?.filter(d => d.date?.startsWith(monthStr)).length || 0;
  
  // Create calendar days array
  const calendarDays = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push(null); // Empty cells before first day
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const hasData = importHistory?.some(d => d.date === dateStr);
    calendarDays.push({ day, dateStr, hasData });
  }
  
  // Navigate months
  const prevMonth = () => {
    setCalendarMonth(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCalendarMonth(new Date(year, month + 1, 1));
  };
  
  // Check if day is weekend
  const isWeekend = (dayIndex) => {
    return dayIndex % 7 === 0 || dayIndex % 7 === 6;
  };

  return (
    <div className="space-y-6" data-testid="imports-history">
      {/* Header */}
      <div className={`${t.card} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-sky-500" />
            <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`}>
              Histórico de Importações
            </h3>
          </div>
          <button
            onClick={onRefresh}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${t.inner} ${t.textSec} hover:opacity-80`}
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${t.inner}`}>
            <div className={`text-xs ${t.textMut} mb-1`}>Total de Dias</div>
            <div className={`text-2xl font-bold ${t.text}`}>{totalDays}</div>
          </div>
          <div className={`p-4 rounded-lg ${t.inner}`}>
            <div className={`text-xs ${t.textMut} mb-1`}>Este Mês</div>
            <div className={`text-2xl font-bold text-sky-500`}>{thisMonthDays}</div>
          </div>
          <div className={`p-4 rounded-lg ${t.inner}`}>
            <div className={`text-xs ${t.textMut} mb-1`}>Primeiro Registo</div>
            <div className={`text-lg font-semibold ${t.text}`}>
              {importHistory?.length > 0 
                ? new Date(importHistory[importHistory.length - 1]?.date).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })
                : '-'
              }
            </div>
          </div>
          <div className={`p-4 rounded-lg ${t.inner}`}>
            <div className={`text-xs ${t.textMut} mb-1`}>Último Registo</div>
            <div className={`text-lg font-semibold ${t.text}`}>
              {importHistory?.length > 0 
                ? new Date(importHistory[0]?.date).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })
                : '-'
              }
            </div>
          </div>
        </div>
        
        {/* Calendar */}
        <div className={`${t.inner} rounded-lg p-4`}>
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={prevMonth}
              className={`p-2 rounded-lg ${t.card} hover:opacity-80`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h4 className={`text-lg font-semibold ${t.text} capitalize`}>
              {monthNames[month]} {year}
            </h4>
            <button
              onClick={nextMonth}
              className={`p-2 rounded-lg ${t.card} hover:opacity-80`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {/* Day names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day, idx) => (
              <div 
                key={day} 
                className={`text-center text-xs font-medium py-2 ${
                  idx === 0 || idx === 6 ? 'text-red-400' : t.textMut
                }`}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayData, idx) => {
              if (!dayData) {
                return <div key={`empty-${idx}`} className="p-2" />;
              }
              
              const { day, dateStr, hasData } = dayData;
              const weekend = isWeekend(idx);
              const isToday = dateStr === new Date().toISOString().slice(0, 10);
              
              return (
                <div
                  key={dateStr}
                  className={`
                    relative p-2 text-center rounded-lg transition-all
                    ${hasData ? 'bg-emerald-500 text-white font-medium cursor-pointer hover:bg-emerald-600' : ''}
                    ${!hasData && weekend ? 'text-red-400/50' : ''}
                    ${!hasData && !weekend ? t.textMut : ''}
                    ${isToday && !hasData ? `ring-2 ring-sky-500 ${t.text}` : ''}
                  `}
                  onClick={() => {
                    if (hasData && onDeleteDay) {
                      if (window.confirm(`Apagar dados de ${new Date(dateStr).toLocaleDateString('pt-PT')}?`)) {
                        onDeleteDay(dateStr);
                      }
                    }
                  }}
                  title={hasData ? `Clique para apagar dados de ${new Date(dateStr).toLocaleDateString('pt-PT')}` : ''}
                >
                  {day}
                  {hasData && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-300 rounded-full" />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 rounded" />
              <span className={`text-xs ${t.textMut}`}>Com dados</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 ${t.card} border rounded`} />
              <span className={`text-xs ${t.textMut}`}>Sem dados</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 ${t.card} ring-2 ring-sky-500 rounded`} />
              <span className={`text-xs ${t.textMut}`}>Hoje</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent imports list */}
      {importHistory && importHistory.length > 0 && (
        <div className={`${t.card} rounded-lg p-6`}>
          <h4 className={`text-sm font-semibold ${t.text} mb-4`}>Últimas Importações</h4>
          
          {/* Header row */}
          <div className={`grid grid-cols-6 gap-2 px-3 py-2 mb-2 text-xs font-semibold ${t.textMut} border-b ${t.border}`}>
            <div>Data</div>
            <div className="text-center">Cortes</div>
            <div className="text-center text-emerald-500">T. Corte</div>
            <div className="text-center text-amber-500">T. Intra Marc.</div>
            <div className="text-center text-red-500">Entre Marc.</div>
            <div className="text-center text-sky-500">Total</div>
          </div>
          
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {[...importHistory].sort((a, b) => b.date.localeCompare(a.date)).map((item, idx) => {
              const totalCuts = item.metrics?.total_cuts || item.total_cuts || 0;
              const cuttingTime = item.metrics?.total_cutting_time || 0;
              const machineStopped = item.metrics?.total_machine_stopped || 0;
              const entreCortes = item.metrics?.total_entre_cortes || item.metrics?.total_between_markers || 0;
              const totalTime = item.metrics?.total_real_time || (cuttingTime + machineStopped + entreCortes);
              return (
              <div 
                key={item.date || idx}
                className={`grid grid-cols-6 gap-2 items-center p-3 rounded-lg ${t.inner} hover:opacity-80`}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-sky-500 flex-shrink-0" />
                  <span className={`text-sm ${t.text} truncate`}>
                    {new Date(item.date).toLocaleDateString('pt-PT', { 
                      weekday: 'short', 
                      day: '2-digit', 
                      month: '2-digit'
                    })}
                  </span>
                </div>
                <div className="text-center">
                  <span className={`text-sm font-semibold ${t.text}`}>{totalCuts}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-mono font-semibold text-emerald-500">{formatMinutesToHHMMSS(cuttingTime)}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-mono font-semibold text-amber-500">{formatMinutesToHHMMSS(machineStopped)}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-mono font-semibold text-red-500">{formatMinutesToHHMMSS(entreCortes)}</span>
                </div>
                <div className="text-center flex items-center justify-center gap-2">
                  <span className="text-sm font-mono font-bold text-sky-500">{formatMinutesToHHMMSS(totalTime)}</span>
                  <button
                    onClick={() => {
                      if (window.confirm(`Apagar dados de ${new Date(item.date).toLocaleDateString('pt-PT')}?`)) {
                        onDeleteDay(item.date);
                      }
                    }}
                    className="p-1 rounded hover:bg-red-500/10 text-red-400 hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportsHistory;
